import { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";

/**
 * Module-level state for the deferred install prompt event
 * ---------------------------------------------------------
 * Chrome/Edge fire `beforeinstallprompt` exactly once per page load,
 * and only if the browser considers the site installable AND the
 * user has some minimum engagement (~30 s of interaction typically).
 * The event MUST be preventDefault()'d and stashed synchronously —
 * you can't retrieve it later. We stash it here, at module scope, so
 * every part of the app (the pill, the admin toolbar button, etc.)
 * can trigger the install using the same captured event.
 *
 * Exports a subscribe() helper so React components can render an
 * install button that only appears once the event has actually fired.
 * Without this, we'd have to react-render before the browser lets us
 * know the site is installable — which by definition can't happen.
 */
let deferredPromptEvent = null;
let installed = false;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn({ deferredPromptEvent, installed }));
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPromptEvent = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferredPromptEvent = null;
    notify();
  });
}

export function useInstallPromptEvent() {
  const [state, setState] = useState({
    deferredPromptEvent,
    installed,
  });
  useEffect(() => {
    listeners.add(setState);
    return () => listeners.delete(setState);
  }, []);
  return state;
}

/**
 * Trigger the browser's native install dialog. Returns the outcome
 * (`accepted` / `dismissed`) or `null` if the event isn't available
 * (e.g. iOS Safari, or the site isn't installable yet).
 */
export async function triggerInstall() {
  if (!deferredPromptEvent) return null;
  try {
    deferredPromptEvent.prompt();
    const { outcome } = await deferredPromptEvent.userChoice;
    // The event is single-use — Chrome will fire another
    // `beforeinstallprompt` on the next reload if the user chose
    // "dismiss" and remains eligible.
    deferredPromptEvent = null;
    notify();
    return outcome;
  } catch {
    return null;
  }
}

/**
 * Human-friendly reason why install isn't available right now.
 * Useful for the diagnostic tooltip on the admin install button.
 */
export function installUnavailableReason() {
  if (typeof window === "undefined") return "Server-side render.";
  if (installed) return "Already installed on this device.";
  if (
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches
  ) {
    return "Already running as an installed app.";
  }
  if (deferredPromptEvent) return "Ready to install.";
  const ua = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(ua);
  if (isIos) {
    return "iOS Safari: tap Share → Add to Home Screen.";
  }
  return (
    "Waiting for the browser to detect the site as installable. " +
    "Keep this tab open for ~30 seconds and interact with a page or two."
  );
}

// -------------------------------------------------------------
// The user-facing pill
// -------------------------------------------------------------

const DISMISSED_KEY = "pepura_pwa_prompt_dismissed_session";
const INSTALLED_KEY = "pepura_pwa_installed";

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
}

export function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

// Chrome/Firefox/Edge on iOS are all just Safari (WebKit) skins under
// Apple's App Store rules — they cannot add-to-home-screen. The
// UA strings still make them recognisable so we can steer the
// customer to Safari instead of showing them a dead-end instruction.
export function isIosNonSafari() {
  if (!isIos() || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Chrome iOS → "CriOS", Firefox iOS → "FxiOS", Edge iOS → "EdgiOS",
  // Opera iOS → "OPiOS", DuckDuckGo → "DuckDuckGo".
  return /CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/.test(ua);
}

// In-app browsers (Instagram, Facebook, Messenger, TikTok, Snapchat…)
// are bare WebViews: no beforeinstallprompt, no Share → Add to Home
// Screen, no install of any kind. The only way to install from there
// is to escape to the system browser first. Detect them so the pill
// gives real instructions instead of a dead-end "Install" button.
export function inAppBrowserName() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  if (/Instagram/i.test(ua)) return "Instagram";
  if (/FBAN|FBAV|FB_IAB|FBIOS|MessengerForiOS|MessengerLite/i.test(ua))
    return "Facebook";
  if (/musical_ly|TikTok|Bytedance/i.test(ua)) return "TikTok";
  if (/Snapchat/i.test(ua)) return "Snapchat";
  if (/LinkedInApp/i.test(ua)) return "LinkedIn";
  return null;
}

// Android WebViews usually honour an intent:// URL that bounces the
// current page out to Chrome. iOS offers no such escape — the customer
// must use the ⋯ menu ("Open in external browser") or paste the link
// into Safari themselves.
function tryOpenInSystemBrowser() {
  const isAndroid = /Android/i.test(navigator.userAgent || "");
  if (!isAndroid) return false;
  const { host, pathname, search } = window.location;
  window.location.href =
    `intent://${host}${pathname}${search}` +
    `#Intent;scheme=https;package=com.android.chrome;end`;
  return true;
}

// Copy the current URL to the customer's clipboard so they can paste
// it into Safari. Falls back to a `prompt()` for older browsers that
// don't expose `navigator.clipboard`.
export async function copyCurrentUrl() {
  const url = window.location.href;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    // Fall through to the prompt fallback.
  }
  try {
    window.prompt("Copy this link and open it in Safari:", url);
    return true;
  } catch {
    return false;
  }
}

/**
 * PWAInstallPrompt — no longer gated on "has placed an order".
 * -------------------------------------------------------------
 * Shows the install pill whenever the browser gives us a chance to
 * install (Chrome/Edge/Samsung Internet fire the deferred prompt event
 * once installability criteria are met, or iOS Safari where we surface
 * the Share → Add-to-Home-Screen hint).
 *
 * Dismissal is now SESSION-scoped, not permanent — the operator asked
 * "why haven't I seen a popup?" and the previous permanent-dismiss
 * flag was one of the reasons. Refreshing the tab OR opening it fresh
 * tomorrow will re-offer it.
 */
export default function PWAInstallPrompt() {
  const { deferredPromptEvent: evt, installed: installedFlag } =
    useInstallPromptEvent();
  const [visible, setVisible] = useState(true);
  const [showIos, setShowIos] = useState(false);
  const [iosNonSafari, setIosNonSafari] = useState(false);
  const [inApp, setInApp] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Session dismissal — cleared when the tab closes.
    if (sessionStorage.getItem(DISMISSED_KEY) === "1") setVisible(false);
    if (localStorage.getItem(INSTALLED_KEY) === "1") setVisible(false);
    if (isStandalone()) setVisible(false);
    if (isIos() && !isStandalone()) setShowIos(true);
    if (isIosNonSafari() && !isStandalone()) setIosNonSafari(true);
    if (!isStandalone()) setInApp(inAppBrowserName());
  }, []);

  const canInstall = Boolean(evt) && !installedFlag;
  const iosHint = showIos && !canInstall;
  const shouldRender = visible && (canInstall || iosHint || Boolean(inApp));

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    const outcome = await triggerInstall();
    if (outcome === "accepted") {
      localStorage.setItem(INSTALLED_KEY, "1");
    }
    setVisible(false);
  };

  const handleCopyLink = async () => {
    const ok = await copyCurrentUrl();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const isAndroid =
    typeof navigator !== "undefined" &&
    /Android/i.test(navigator.userAgent || "");

  // Memoize the copy — recomputed only when installability state
  // flips, not on every parent render.
  const copy = useMemo(() => {
    if (inApp) {
      return isAndroid
        ? `${inApp}'s built-in browser can't install apps. Open this page in Chrome to install Pepura.`
        : `${inApp}'s built-in browser can't install apps. Tap the ⋯ menu and choose "Open in external browser", then in Safari tap Share → Add to Home Screen.`;
    }
    if (canInstall) {
      return "Skip the browser next time — install Pepura to your home screen.";
    }
    if (iosNonSafari) {
      return (
        "iOS only lets Safari install apps. Copy this page's link, open Safari, " +
        "paste the link, then tap Share → Add to Home Screen."
      );
    }
    return (
      "In Safari, tap the Share icon then Add to Home Screen for one-tap re-orders."
    );
  }, [canInstall, iosNonSafari, inApp, isAndroid]);

  if (!shouldRender) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Install Pepura to your device"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[95] pointer-events-none"
    >
      <div className="pointer-events-auto rounded-xl border border-white/15 bg-[#0f1418]/95 shadow-xl backdrop-blur-sm p-4 text-white">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#20a6da]/15 text-[#20a6da] shrink-0">
            <Download size={18} strokeWidth={2.25} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm">
              {inApp
                ? "Install via your browser"
                : iosNonSafari
                  ? "Install Pepura via Safari"
                  : "Install Pepura"}
            </p>
            <p className="mt-1 text-xs text-[#bfcde3] leading-snug">{copy}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {inApp && isAndroid && (
                <button
                  type="button"
                  data-testid="pwa-open-in-browser-btn"
                  onClick={() => tryOpenInSystemBrowser()}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#20a6da] text-[#0a0a0a] hover:bg-[#1c92bf] px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
                >
                  Open in Chrome
                </button>
              )}
              {inApp && (
                <button
                  type="button"
                  data-testid="pwa-inapp-copy-link-btn"
                  onClick={handleCopyLink}
                  className={
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wide " +
                    (isAndroid
                      ? "border border-white/20 text-[#bfcde3] hover:text-white hover:bg-white/5"
                      : "bg-[#20a6da] text-[#0a0a0a] hover:bg-[#1c92bf]")
                  }
                >
                  {copied ? "Link copied ✓" : "Copy link"}
                </button>
              )}
              {canInstall && !inApp && (
                <button
                  type="button"
                  data-testid="pwa-install-btn"
                  onClick={install}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#20a6da] text-[#0a0a0a] hover:bg-[#1c92bf] px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
                >
                  <Download size={13} strokeWidth={2.5} />
                  Install
                </button>
              )}
              {iosNonSafari && !inApp && (
                <button
                  type="button"
                  data-testid="pwa-copy-link-btn"
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#20a6da] text-[#0a0a0a] hover:bg-[#1c92bf] px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
                >
                  {copied ? "Link copied ✓" : "Copy link"}
                </button>
              )}
              <button
                type="button"
                data-testid="pwa-dismiss-btn"
                onClick={dismiss}
                className="inline-flex items-center gap-1.5 rounded-md border border-white/20 bg-transparent text-[#bfcde3] hover:text-white hover:bg-white/5 px-3 py-1.5 text-xs font-medium uppercase tracking-wide"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
